import { RunState, RunType } from "types/generated";
import { AccessResult } from "utils/access";

const STACK_DELETING_MESSAGE = "You can't do this while the stack is being deleted";

const HIDDEN: AccessResult = { result: false };

export type KnownBlockerKind = "localSource" | "moduleVersion" | "missingCommit";

// shortcut: matches the backend CanBeRetried() strings because Run exposes no structured
// reason; a reworded string falls back to the generic blocker. Upgrade: a structured field.
const KNOWN_BLOCKERS: Partial<Record<string, KnownBlockerKind>> = {
  "runs based on the local source code cannot be retried": "localSource",
  "cannot retry a module testing run that belongs to a version": "moduleVersion",
  "stack commit is not set, cannot retry the run": "missingCommit",
};

export type RetryBlockedReason =
  | { kind: "stackMoved" }
  | { kind: KnownBlockerKind }
  | { kind: "backendBlocker"; message: string };

type GetRunActionsInput = {
  runType: RunType;
  runState: RunState;
  isRunFinished: boolean;
  isStackDeleting: boolean;
  runCommitHash: string | undefined;
  stackTrackedCommitHash: string | undefined;
  runRetryBlocker: string | null;
  canRetry: AccessResult;
  canTrigger: AccessResult | undefined;
  canPerformAgain: AccessResult | undefined;
};

type RetryState = {
  retry: AccessResult;
  retryBlockedReason?: RetryBlockedReason;
};

type RunActions = RetryState & {
  trigger: AccessResult;
  performAgain: AccessResult;
};

// The backend retries a retryable run in any terminal state; these are the states the
// run page has always offered it in.
const TRACKED_RETRY_STATES = [RunState.Failed, RunState.Stopped, RunState.Discarded];
const PROPOSED_RETRY_STATES = [
  RunState.Failed,
  RunState.Stopped,
  RunState.Discarded,
  RunState.Finished,
];
// Canceled since #7809.
const MODULE_TEST_RETRY_STATES = [
  RunState.Failed,
  RunState.Stopped,
  RunState.Discarded,
  RunState.Canceled,
];

const getOfferedRetryState = (
  { runState, isStackDeleting, runRetryBlocker, canRetry }: GetRunActionsInput,
  offeredStates: RunState[]
): RetryState => {
  if (!offeredStates.includes(runState)) {
    return { retry: HIDDEN };
  }

  if (isStackDeleting) {
    return { retry: { result: false, message: STACK_DELETING_MESSAGE } };
  }

  const knownBlockerKind = runRetryBlocker ? KNOWN_BLOCKERS[runRetryBlocker] : undefined;

  if (knownBlockerKind) {
    return { retry: HIDDEN, retryBlockedReason: { kind: knownBlockerKind } };
  }

  if (runRetryBlocker) {
    return {
      retry: HIDDEN,
      retryBlockedReason: { kind: "backendBlocker", message: runRetryBlocker },
    };
  }

  return { retry: canRetry };
};

const getTrackedRunRetryState = (input: GetRunActionsInput): RetryState => {
  const { runCommitHash, stackTrackedCommitHash } = input;

  // The stack has deployed a newer commit, so the backend would refuse this retry.
  // Compared structurally rather than by matching the blocker string, which is unversioned.
  if (runCommitHash && stackTrackedCommitHash && runCommitHash !== stackTrackedCommitHash) {
    return { retry: HIDDEN, retryBlockedReason: { kind: "stackMoved" } };
  }

  return getOfferedRetryState(input, TRACKED_RETRY_STATES);
};

const getRetryState = (input: GetRunActionsInput): RetryState => {
  if (!input.isRunFinished) {
    return { retry: HIDDEN };
  }

  switch (input.runType) {
    case RunType.Tracked:
      return getTrackedRunRetryState(input);
    case RunType.Proposed:
      return getOfferedRetryState(input, PROPOSED_RETRY_STATES);
    case RunType.Testing:
      return getOfferedRetryState(input, MODULE_TEST_RETRY_STATES);
    // Mirrors the allow-list switch in backend/database/run.go CanBeRetried() - these
    // run types can never be retried, so the control is hidden rather than disabled. The
    // default catches a type newer than the generated enum.
    case RunType.Task:
    case RunType.Destroy:
    case RunType.Parse:
    case RunType.Unknown:
    default:
      return { retry: HIDDEN };
  }
};

const blockWhileStackDeletes = (
  access: AccessResult | undefined,
  isStackDeleting: boolean
): AccessResult => {
  // An already-hidden action stays hidden: giving it a reason would surface a
  // control that was never meant to render.
  if (!access || (!access.result && !access.message)) {
    return HIDDEN;
  }

  if (isStackDeleting) {
    return { result: false, message: STACK_DELETING_MESSAGE };
  }

  return access;
};

/**
 * Single source of truth for whether the run page may offer Retry, Trigger and Perform
 * again, and why Retry is missing when it is. Each action resolves to one AccessResult,
 * encoded per utils/access.ts.
 */
const getRunActions = (input: GetRunActionsInput): RunActions => ({
  ...getRetryState(input),
  trigger: blockWhileStackDeletes(input.canTrigger, input.isStackDeleting),
  performAgain: blockWhileStackDeletes(input.canPerformAgain, input.isStackDeleting),
});

export default getRunActions;
